import { useCallback, useMemo, useState } from 'react'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import type { GridColumn, GridSortDirection, GridSortModelItem } from './types'

export interface GridLeafColumn<T> {
  column: GridColumn<T>
  field: string
  width: number
  offset: number
  pinned?: 'left' | 'right'
  /** True for the last left-pinned / first right-pinned column — draws the edge shadow. */
  isPinnedEdge: boolean
}

/**
 * Distributes the available width the way ag-grid does: fixed columns take
 * their own width, `flex` columns share what is left in proportion, clamped to
 * min/max. A user resize pins that column to an explicit width, so it stops
 * flexing.
 */
export const computeColumnWidths = <T,>(
  columns: GridColumn<T>[],
  containerWidth: number,
  overrides: Record<string, number>,
): number[] => {
  const widths = columns.map((col) => {
    const override = overrides[col.field]
    if (override != null) return override
    if (col.flex) return null
    return col.width ?? col.minWidth ?? 150
  })

  const flexTotal = columns.reduce(
    (sum, col, i) => (widths[i] === null ? sum + (col.flex ?? 0) : sum),
    0,
  )

  if (flexTotal > 0 && containerWidth > 0) {
    const fixed = widths.reduce<number>((sum, w) => sum + (w ?? 0), 0)
    const spare = Math.max(0, containerWidth - fixed)

    columns.forEach((col, i) => {
      if (widths[i] !== null) return
      const share = (spare * (col.flex ?? 0)) / flexTotal
      const min = col.minWidth ?? 80
      const max = col.maxWidth ?? Infinity
      widths[i] = Math.min(Math.max(share, min), max)
    })
  }

  // No container measurement yet (or no flex columns): fall back to the min so
  // the first paint is not zero-width.
  return columns.map((col, i) => widths[i] ?? col.minWidth ?? 150)
}

/**
 * Sorts rows for the current sort model, then hands them to `postSort`.
 * `postSort` is what keeps a create row last regardless of the active sort.
 */
export const applySort = <T,>(
  rows: T[],
  columns: GridColumn<T>[],
  sorting: { field: string; desc: boolean } | null,
  postSort?: (rows: T[]) => T[],
): T[] => {
  let out = rows

  if (sorting) {
    const col = columns.find((c) => c.field === sorting.field)
    const getValue = (row: T) =>
      col?.valueGetter ? col.valueGetter(row) : (row as Record<string, unknown>)[sorting.field]

    out = [...rows].sort((a, b) => {
      const result = col?.comparator
        ? col.comparator(getValue(a), getValue(b), a, b)
        : defaultComparator(getValue(a), getValue(b))
      return sorting.desc ? -result : result
    })
  }

  return postSort ? postSort(out) : out
}

/** Nulls last, numbers numerically, everything else case-insensitively. */
export const defaultComparator = (a: unknown, b: unknown): number => {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1

  if (typeof a === 'number' && typeof b === 'number') return a - b

  const aNum = Number(a)
  const bNum = Number(b)
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum

  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
}

/**
 * Applies a restored column order.
 *
 * The stored order names the columns it knew about; anything added since (a new
 * column def, or one that only appears in a particular mode — the bulk-edit
 * checkbox) takes the place its definition asks for, immediately after the
 * column it was declared after, rather than being pushed to the end.
 */
export const applyColumnOrder = <T,>(
  columns: GridColumn<T>[],
  columnOrder: string[] | null,
): GridColumn<T>[] => {
  if (!columnOrder) return columns

  const rank = new Map(columnOrder.map((field, index) => [field, index]))
  let previous = -1
  const effectiveRank = columns.map((column) => {
    const known = rank.get(column.field)
    if (known !== undefined) {
      previous = known
      return known
    }
    return previous + 0.5
  })

  return columns
    .map((column, index) => ({ column, index }))
    .sort(
      (a, b) => effectiveRank[a.index] - effectiveRank[b.index] || a.index - b.index,
    )
    .map((entry) => entry.column)
}

interface UseGridStateArgs<T> {
  columns: GridColumn<T>[]
  rows: T[]
  getRowId: (row: T) => string
  postSort?: (rows: T[]) => T[]
  /** Server-side row model: the rows arrive already sorted, so leave them be. */
  sortOnServer?: boolean
  /** Sort applied on mount. */
  defaultSort?: GridSortModelItem
}

export const useGridState = <T,>({
  columns,
  rows,
  getRowId,
  postSort,
  sortOnServer = false,
  defaultSort,
}: UseGridStateArgs<T>) => {
  const [sorting, setSorting] = useState<SortingState>(() =>
    defaultSort ? [{ id: defaultSort.field, desc: defaultSort.sort === 'desc' }] : [],
  )
  const [widthOverrides, setWidthOverrides] = useState<Record<string, number>>({})
  const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({})
  const [columnOrder, setColumnOrder] = useState<string[] | null>(null)

  const orderedColumns = useMemo(
    () => applyColumnOrder(columns, columnOrder),
    [columns, columnOrder],
  )

  const visibleColumns = useMemo(
    () => orderedColumns.filter((col) => !col.hide && !hiddenFields[col.field]),
    [orderedColumns, hiddenFields],
  )

  // TanStack owns the row model. Sorting is resolved through our own comparator
  // path (`applySort`) so `postSort` can run after it — the table's sorted row
  // model has no equivalent hook.
  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      visibleColumns.map((col) => ({
        id: col.field,
        accessorFn: (row: T) =>
          col.valueGetter
            ? col.valueGetter(row)
            : (row as Record<string, unknown>)[col.field],
        enableSorting: col.sortable !== false,
      })),
    [visibleColumns],
  )

  const table = useReactTable<T>({
    data: rows,
    columns: tableColumns,
    getRowId,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  })

  const activeSort = useMemo(
    () => (sorting.length > 0 ? { field: sorting[0].id, desc: sorting[0].desc } : null),
    [sorting],
  )

  const displayedRows = useMemo(
    () => applySort(rows, visibleColumns, sortOnServer ? null : activeSort, postSort),
    [rows, visibleColumns, activeSort, postSort, sortOnServer],
  )

  const getSortDirection = useCallback(
    (field: string): GridSortDirection => {
      const entry = sorting.find((s) => s.id === field)
      if (!entry) return null
      return entry.desc ? 'desc' : 'asc'
    },
    [sorting],
  )

  /** asc → desc → unsorted, matching ag-grid's `unSortIcon` three-state header. */
  const toggleSort = useCallback((field: string) => {
    setSorting((prev) => {
      const entry = prev.find((s) => s.id === field)
      if (!entry) return [{ id: field, desc: false }]
      if (!entry.desc) return [{ id: field, desc: true }]
      return []
    })
  }, [])

  const setColumnWidth = useCallback((field: string, width: number) => {
    setWidthOverrides((prev) => ({ ...prev, [field]: width }))
  }, [])

  const setColumnsVisible = useCallback((fields: string[], visible: boolean) => {
    setHiddenFields((prev) => {
      const next = { ...prev }
      fields.forEach((field) => {
        if (visible) delete next[field]
        else next[field] = true
      })
      return next
    })
  }, [])

  return {
    table,
    sorting,
    setSorting,
    activeSort,
    displayedRows,
    orderedColumns,
    visibleColumns,
    columnOrder,
    setColumnOrder,
    getSortDirection,
    toggleSort,
    widthOverrides,
    setWidthOverrides,
    setColumnWidth,
    hiddenFields,
    setHiddenFields,
    setColumnsVisible,
  }
}
